import { useState } from "react";
import { StyleSheet, Switch, type SwitchProps, View, type ViewStyle } from "react-native";
import type { StyleProp } from "react-native/Libraries/StyleSheet/StyleSheet";
import { FieldLabel, type FieldLabelProps } from "~/components/form/FieldLabel";
import { ErrorMessage } from "~/components/layout/text/ErrorMessage";
import { FieldHint } from "~/components/layout/text/FieldHint";
import { Only } from "~/components/permission/Only";

import { Space } from "~/constants/sizing";
import { Leading } from "~/constants/typography";
import { useTheme } from "~/providers/ThemeProvider";
import { setWidthOnLayout } from "~/util/layout";

export type SwitchFieldProps = {
    containerStyle?: StyleProp<ViewStyle>;
    labelContainerStyle?: StyleProp<ViewStyle>;
} & Omit<FieldLabelProps, "children"> &
    SwitchProps;

export const SWITCH_WIDTH = 48;

export const SwitchField = ({
    label,
    error,
    hint,
    hideLabel,
    optional,
    style,
    containerStyle,
    labelContainerStyle,
    labelStyle,
    disabled,
    onLayout,
    icon,
    iconProps,
    ...switchProps
}: SwitchFieldProps) => {
    const { colours } = useTheme();
    const [width, setWidth] = useState(300);
    const handleLayout = setWidthOnLayout(setWidth, onLayout);
    const label_width = Math.floor(width - (Space.sm + SWITCH_WIDTH));

    const { trackColor, thumbColor, ios_backgroundColor } = switchProps;

    return (
        <View style={[styles.container, containerStyle]} onLayout={handleLayout}>
            <FieldLabel
                label={label}
                hideLabel={hideLabel}
                optional={optional}
                style={[styles.body, { width }, style]}
                labelStyle={[Leading.base, labelStyle]}
                labelContainerStyle={[styles.label, { width: label_width }, labelContainerStyle]}
                icon={icon}
                iconProps={iconProps}
            >
                <View>
                    <Switch
                        {...switchProps}
                        disabled={disabled}
                        trackColor={
                            trackColor ??
                            (disabled
                                ? { false: colours.toggle.off, true: colours.toggle.off }
                                : { false: colours.toggle.off, true: colours.toggle.on })
                        }
                        thumbColor={thumbColor ?? (disabled ? colours.toggle.disabled : colours.toggle.indicator)}
                        ios_backgroundColor={ios_backgroundColor ?? colours.toggle.off}
                    />
                </View>
            </FieldLabel>
            <Only when={hint}>
                <FieldHint>{hint}</FieldHint>
            </Only>
            <Only when={error}>
                <ErrorMessage>{error}</ErrorMessage>
            </Only>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        alignItems: "stretch",
    },
    body: {
        flexDirection: "row",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: Space.sm,
    },
    label: {
        justifyContent: "center",
    },
});
